import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/toaster";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Clock,
  Crown,
  Lock,
  Quote,
  Sprout,
} from "lucide-react";
import { courseModules, courseStats, STRIPE_CHECKOUT_URL } from "../mock";
import CreditMasteryPremium from "../components/CreditMasteryPremium";

const FinanceCoursePage = () => {
  const [enrolled, setEnrolled] = useState(false);

  const enroll = () => {
    const list = JSON.parse(
      localStorage.getItem("good_fruit_enrolled") || "[]"
    );
    if (!list.includes("finance-course")) {
      list.push("finance-course");
      localStorage.setItem("good_fruit_enrolled", JSON.stringify(list));
    }
    setEnrolled(true);
  };

  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#04181a] to-black" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 25%, #2dd4bf 0%, transparent 40%), radial-gradient(circle at 85% 65%, #14b8a6 0%, transparent 45%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-teal-300 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs tracking-wide">
            <BookOpen className="w-3.5 h-3.5" />
            <span>The Good Fruit Finance Course</span>
          </div>
          <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05] max-w-4xl">
            Biblical money.{" "}
            <em className="text-teal-300 font-serif font-normal">
              Practical results.
            </em>
          </h1>
          <p className="mt-5 text-white/65 text-lg max-w-2xl">
            8 modules · 56+ lessons · self-paced. Built on scripture, designed
            for real life — with workbooks, accountability, and a community
            walking with you.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            {enrolled ? (
              <div className="inline-flex items-center gap-2 bg-teal-400/15 border border-teal-400/30 text-teal-200 rounded-md px-5 py-3">
                <CheckCircle2 className="w-5 h-5" />
                <span>Enrolled. Module 1 awaits.</span>
              </div>
            ) : (
              <Button
                onClick={enroll}
                className="bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 px-6"
              >
                <Sprout className="w-4 h-4 mr-2" />
                Enroll free
              </Button>
            )}
            <Link to="/funding">
              <Button
                variant="outline"
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 hover:text-teal-300 h-12 px-6"
              >
                Looking for funding instead?
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-xl overflow-hidden border border-white/10">
            {courseStats.map((s) => (
              <div key={s.label} className="bg-black/60 p-5 md:p-6">
                <div className="text-3xl md:text-4xl font-semibold text-teal-300">
                  {s.value}
                </div>
                <div className="mt-1 text-white/60 text-xs uppercase tracking-wide">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="bg-white text-black py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            The full curriculum
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-black leading-tight max-w-3xl">
            8 modules to bear{" "}
            <em className="text-teal-700 font-serif font-normal">good fruit</em>{" "}
            with your money.
          </h2>

          <div className="mt-12 space-y-5">
            {courseModules.map((m) => {
              const isDark = m.color === "black";
              return (
                <article
                  key={m.id}
                  className={`group rounded-2xl overflow-hidden border transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_50px_-25px_rgba(0,0,0,0.25)] ${
                    isDark
                      ? "bg-black text-white border-black"
                      : "bg-white text-black border-black/10"
                  }`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-7 md:p-9">
                    <div className="md:col-span-2">
                      <div
                        className={`text-5xl md:text-6xl font-semibold tracking-tight ${
                          isDark ? "text-teal-300" : "text-teal-700"
                        }`}
                      >
                        {m.number}
                      </div>
                      <div
                        className={`mt-2 inline-flex items-center gap-1.5 text-xs ${
                          isDark ? "text-white/60" : "text-black/55"
                        }`}
                      >
                        <Clock className="w-3.5 h-3.5" />
                        {m.duration} · {m.lessons} lessons
                      </div>
                    </div>
                    <div className="md:col-span-6">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <h3 className="text-2xl md:text-3xl font-semibold tracking-tight">
                          {m.title}
                        </h3>
                        {m.premium && (
                          <a
                            href={STRIPE_CHECKOUT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full whitespace-nowrap transition-colors ${
                              isDark
                                ? "bg-teal-400 text-black hover:bg-teal-300"
                                : "bg-black text-teal-300 border border-teal-400/40 hover:bg-teal-700"
                            }`}
                          >
                            <Crown className="w-3 h-3" />
                            Unlock · $29.95/mo
                          </a>
                        )}
                      </div>
                      <div
                        className={`mt-3 flex items-start gap-2 text-sm border-l-2 pl-3 ${
                          isDark
                            ? "border-teal-400/50 text-teal-200/90"
                            : "border-teal-600/40 text-teal-800"
                        }`}
                      >
                        <Quote
                          className={`w-3.5 h-3.5 mt-1 ${
                            isDark ? "text-teal-300/70" : "text-teal-600/60"
                          }`}
                        />
                        <span>
                          <strong className="font-semibold">{m.verse}</strong>{" "}
                          —{" "}
                          <em className="font-serif">{m.verseText}</em>
                        </span>
                      </div>
                      <p
                        className={`mt-4 leading-relaxed ${
                          isDark ? "text-white/70" : "text-black/65"
                        }`}
                      >
                        {m.summary}
                      </p>
                    </div>
                    <div className="md:col-span-4">
                      <div
                        className={`text-xs uppercase tracking-wide font-medium ${
                          isDark ? "text-teal-300" : "text-teal-700"
                        }`}
                      >
                        Practical applications
                      </div>
                      <ul className="mt-3 space-y-2">
                        {m.practical.map((step) => (
                          <li
                            key={step}
                            className={`flex items-start gap-2.5 text-sm ${
                              isDark ? "text-white/85" : "text-black/75"
                            }`}
                          >
                            <span
                              className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                                isDark
                                  ? "bg-teal-400 text-black"
                                  : "bg-teal-500/15 text-teal-700"
                              }`}
                            >
                              <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                            </span>
                            {step}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mt-14 rounded-2xl bg-black text-white p-8 md:p-12 text-center">
            <div className="text-xs uppercase tracking-[0.18em] text-teal-300 font-medium">
              Ready to start
            </div>
            <h3 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight max-w-2xl mx-auto">
              Bear good fruit with the way you handle money.
            </h3>
            <div className="mt-7">
              {enrolled ? (
                <div className="inline-flex items-center gap-2 bg-teal-400/15 border border-teal-400/30 text-teal-200 rounded-md px-5 py-3">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>You&apos;re enrolled — watch your inbox.</span>
                </div>
              ) : (
                <Button
                  onClick={enroll}
                  className="bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 px-7"
                >
                  Enroll free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Premium Credit Mastery */}
      <CreditMasteryPremium />

      <Footer />
      <Toaster />
    </div>
  );
};

export default FinanceCoursePage;
